import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  CreditCard,
  Package,
  Building2,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  Briefcase,
  Shield,
  QrCode,
  Receipt,
  FileText,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { NavBar, NavItem } from './NavBar';
import { AdminNavBarHeader } from './AdminNavBarHeader';
import { useSidebar } from '../../context/SidebarContext';
import { useIsDeveloper } from '../../hooks/useIsDeveloper';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/Button';

interface AdminNavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  isAvailable?: boolean;
  forAdmin?: boolean;
  forDeveloper?: boolean;
}

const adminNavItems: AdminNavItem[] = [
  {
    path: '/admin/dashboard',
    label: 'Dashboard',
    icon: <Home className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
    forDeveloper: true,
  },
  {
    path: '/admin/stripe-management',
    label: 'Stripe Management',
    icon: <CreditCard className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/fee-schedules',
    label: 'Fee Schedules',
    icon: <Receipt className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/rollops-subscriptions',
    label: 'RollOps Subscriptions',
    icon: <Package className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/subscription-management',
    label: 'Subscription Management',
    icon: <CreditCard className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/customers',
    label: 'Customer Dashboard',
    icon: <Building2 className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/jobs',
    label: 'Job Board',
    icon: <Briefcase className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/admin-management',
    label: 'Admin Access',
    icon: <Shield className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/qr-generator',
    label: 'QR Generator',
    icon: <QrCode className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/admin/default-waiver',
    label: 'Default Waiver',
    icon: <FileText className="w-5 h-5" />,
    isAvailable: true,
    forAdmin: true,
  },
  {
    path: '/dev/companies',
    label: 'Company Search',
    icon: <Building2 className="w-5 h-5" />,
    isAvailable: true,
    forDeveloper: true,
  },
  {
    path: '/dev/impersonate',
    label: 'User Impersonation',
    icon: <Users className="w-5 h-5" />,
    isAvailable: true,
    forDeveloper: true,
  },
  {
    path: '/admin/analytics',
    label: 'Analytics',
    icon: <BarChart3 className="w-5 h-5" />,
    isAvailable: false,
    forAdmin: true,
    forDeveloper: true,
  },
  {
    path: '/admin/settings',
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
    isAvailable: false,
    forAdmin: true,
    forDeveloper: true,
  },
];

/**
 * Host wrapper for Admin navigation.
 * 
 * Provides:
 * - Admin-specific nav items
 * - Role-based filtering (admin/developer)
 * - Styling consistent with admin theme
 * - Collapse state management
 * - Active route detection
 */
export function AdminNavHost() {
  const { isOpen, toggle } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDeveloper } = useIsDeveloper();
  const { adminUser } = useAdminAuth();
  const isAdmin = !!adminUser;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // Filter items by role and availability
  const visibleItems = adminNavItems.filter((item) => {
    if (!item.isAvailable) return true; // Show unavailable items (with disabled state)
    
    const hasAccess = 
      (item.forAdmin && isAdmin) || 
      (item.forDeveloper && isDeveloper) ||
      (!item.forAdmin && !item.forDeveloper);
    
    return hasAccess;
  });

  // Convert to NavItem format with active state
  const navItems: NavItem[] = visibleItems.map((item) => {
    const isActive = location.pathname === item.path || 
      (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
    
    return {
      key: item.path,
      label: item.label,
      href: item.path,
      icon: item.icon,
      active: isActive,
      disabled: !item.isAvailable,
    };
  });

  // Header with logo and toggle - uses shared AdminNavBarHeader component
  const header = (
    <AdminNavBarHeader
      leftContent={
        isOpen ? (
          <Link to="/admin/dashboard" className="flex items-center space-x-3 flex-1">
            <img 
              src="/rollops-logo.png" 
              alt="RollOps Logo" 
              width={32} 
              height={32} 
              className="w-8 h-8 flex-shrink-0" 
            />
            <span className="text-xl font-bold transition-all duration-300 text-[#EBFFEE] flex items-center">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
            <span className="text-xs text-[#A9A3A3] ml-2 flex items-center">Backend</span>
          </Link>
        ) : null
      }
      rightContent={
        <button
          onClick={toggle}
          className="w-8 h-8 flex items-center justify-center text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
          aria-label="Toggle sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      }
      collapsed={!isOpen}
    />
  );

  // Footer with sign out
  const footer = (
    <div className={`border-t border-[#504A4A] ${isOpen ? 'px-3 py-3' : 'px-0 py-3 flex justify-center'}`}>
      <Button
        onClick={handleSignOut}
        className={`
          bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] transition-all duration-300 flex items-center py-2 rounded-lg relative
          ${isOpen 
            ? 'w-full justify-center' 
            : 'w-10 h-10'
          }
        `}
        title={!isOpen ? 'Sign Out' : undefined}
      >
        <span 
          className={`
            flex-shrink-0 w-4 h-4 flex items-center justify-center absolute top-1/2 -translate-y-1/2
            transition-all duration-300
            ${isOpen 
              ? 'left-3' 
              : 'left-1/2 -translate-x-1/2'
            }
          `}
        >
          <LogOut className="w-4 h-4" />
        </span>
        <span 
          className={`
            text-sm transition-all duration-300 whitespace-nowrap
            ${isOpen 
              ? 'opacity-100 max-w-full' 
              : 'opacity-0 max-w-0 overflow-hidden'
            }
          `}
        >
          Sign Out
        </span>
      </Button>
    </div>
  );

  return (
    <aside 
      className={`
        fixed left-0 bg-[#221F1F] border-r border-[#504A4A] z-40
        transition-all duration-300 ease-in-out font-['Inter']
        ${isOpen ? 'w-64 translate-x-0' : 'w-16 -translate-x-full md:translate-x-0'}
        top-14 h-[calc(100vh-3.5rem)] md:top-0 md:h-full
      `}
    >
      <NavBar
        items={navItems}
        header={header}
        footer={footer}
        collapsed={!isOpen}
        className=""
        listClassName={isOpen ? 'px-3' : 'px-2'}
        itemBaseClassName="flex items-center transition-all duration-200 py-2 rounded-lg relative"
        itemActiveClassName="bg-[#9B1421] text-[#EBFFEE]"
        itemInactiveClassName="text-[#A9A3A3] hover:bg-[#363232] hover:text-[#EBFFEE]"
        itemDisabledClassName="cursor-not-allowed text-[#706A6A]"
        badgeClassName="bg-[#363232] text-[#EBFFEE]"
        ariaLabel="Admin Navigation"
      />
    </aside>
  );
}
